import {
    ACCEPT_ALL_BUTTON_SELECTOR,
    ACCEPT_BUTTON_SELECTOR,
    ACCEPT_NECESSARY_BUTTON_SELECTOR,
    ADVANCED_VIEW_SELECTOR,
    BASIC_VIEW_SELECTOR,
    CONSENT_CHECKBOX_SELECTOR,
    CONSENT_MODAL_ID,
    HIDE_CLASS,
    POPUP_CLASS,
    SELECT_ALL_CONSENTS_SELECTOR,
    SHOW_ADVANCED_VIEW_SELECTOR,
    SHOW_BASIC_VIEW_SELECTOR
} from './constants';
import { CONSENT_MODAL_COUNTER, CONSENT_MODAL_KEY, LIST_OF_CONSENT_NAMES } from '../../constants';
import { checkCheckboxes, uncheckCheckboxes } from '@core/dom_utilities/forms';

import { $emit } from '@core/tools/event_bus';
import { AfterConsentsSaved } from './events/after_consents_saved';
import { CONSENT_MODAL_CREATED } from './event_names';
import { ConsentsApi } from '../consents_api';
import { TConsentName } from '../../types';
import { getTemplate } from '@core/dom_utilities/get';
import Features from '@core/tools/features';
import { UiDomUtils } from '@dreamcommerce/utilities';

export class ConsentsModal {
    private _api: ConsentsApi;
    private _modal: HTMLElement | null;

    constructor(api: ConsentsApi) {
        this._api = api;
        this._modal = null;

        if (Features.isEnabled('cookie_settings_link')) {
            if (!this._hasSavedConsents()) {
                this._initConsentsModal();
            } else if (window.location.hash === '#cookies-settings') {
                this._initConsentsModal();

                this.showAdvancedView();
            }

            $('[href*="#cookies-settings"]').on('click', (ev) => {
                ev.preventDefault();

                this._initConsentsModal();

                this.showAdvancedView();
            });
        } else {
            if (!this._hasSavedConsents()) {
                this._initConsentsModal();
            }
        }
    }

    private _hasSavedConsents(): boolean {
        const userModalCounter = this.loadDisplayCounter();
        return !(userModalCounter !== CONSENT_MODAL_COUNTER);
    }

    private _initConsentsModal() {
        this._render();
        this._updateConsentCheckboxes();
        this._bindEvents();
        new AfterConsentsSaved(this);

        $emit(CONSENT_MODAL_CREATED, this);
    }

    private _render(): void {
        this._modal = getTemplate(CONSENT_MODAL_ID)[0];

        document.body.appendChild(this._modal);

        this._addInertToBodyChildrenElements();
    }

    private _updateConsentCheckboxes(): void {
        this._api.getGrantedConsents().forEach((consent) => {
            const $consentCheckbox = document.querySelector(`input[name="${consent.getName()}"]`) as HTMLInputElement;
            $consentCheckbox.checked = true;
        });
    }

    private _bindEvents(): void {
        document.querySelector(ACCEPT_BUTTON_SELECTOR)?.addEventListener('click', this.saveConsentsChoosenByCustomer.bind(this));

        document.querySelector(ACCEPT_ALL_BUTTON_SELECTOR)?.addEventListener('click', () => {
            this._api.grantConsents(LIST_OF_CONSENT_NAMES);
            this._api.saveConsents();
        });

        if (Features.isEnabled('cookie_settings_link')) {
            if (this._hasSavedConsents()) {
                document.querySelector(SHOW_BASIC_VIEW_SELECTOR)?.addEventListener('click', this.close.bind(this));
            } else {
                document.querySelector(SHOW_BASIC_VIEW_SELECTOR)?.addEventListener('click', this.showBasicView.bind(this));
            }
        } else {
            document.querySelector(SHOW_BASIC_VIEW_SELECTOR)?.addEventListener('click', this.showBasicView.bind(this));
        }
        document.querySelector(SHOW_ADVANCED_VIEW_SELECTOR)?.addEventListener('click', this.showAdvancedView.bind(this));
        document.querySelector(SELECT_ALL_CONSENTS_SELECTOR)?.addEventListener('change', this.toggleAllConsents.bind(this));

        if (Features.isEnabled('customer_privacy_v2')) {
            document.querySelector(ACCEPT_NECESSARY_BUTTON_SELECTOR)?.addEventListener('click', this.grantNecessaryConsents.bind(this));
        }

        this._modal?.querySelector<HTMLElement>(BASIC_VIEW_SELECTOR)?.addEventListener('keydown', this._handleTabNavigation);

        this._modal?.querySelector<HTMLElement>(ADVANCED_VIEW_SELECTOR)?.addEventListener('keydown', this._handleTabNavigation);
    }

    private _handleTabNavigation = (ev: KeyboardEvent): void => {
        if (ev.key !== 'Tab' || !ev.currentTarget) return;
        const $modalContainerElement = ev.currentTarget as HTMLElement;

        const $firstElement = UiDomUtils.getFocusableElement($modalContainerElement);
        const $lastElement = UiDomUtils.getLastFocusableElement($modalContainerElement);

        if (ev.shiftKey) {
            if (ev.target === $firstElement) {
                ev.preventDefault();
                $lastElement?.focus();
            }
        } else {
            if (ev.target === $lastElement) {
                ev.preventDefault();
                $firstElement?.focus();
            }
        }
    };

    private _addInertToBodyChildrenElements(): void {
        [...document.body.children].forEach((child) => {
            if (![...child.classList].some((className) => className.includes('consents'))) {
                child.setAttribute('inert', '');
            }
        });
    }

    private _removeInertFromBodyChildrenElements(): void {
        [...document.body.children].forEach((child) => {
            child.removeAttribute('inert');
        });
    }

    grantNecessaryConsents(): void {
        this._api.grantConsents([]);
        this._api.saveConsents();
    }

    saveConsentsChoosenByCustomer(): void {
        const grantedConsents: TConsentName[] = [];
        const withdrawnConsents: TConsentName[] = [];
        const $checkboxConsents = [...document.querySelectorAll(CONSENT_CHECKBOX_SELECTOR)] as HTMLInputElement[];

        $checkboxConsents.forEach(($checkbox: HTMLInputElement) => {
            const consentName = $checkbox.name as TConsentName;
            if ($checkbox.checked) {
                grantedConsents.push(consentName);
            } else {
                withdrawnConsents.push(consentName);
            }
        });

        this._api.grantConsents(grantedConsents);
        this._api.withdrawConsents(withdrawnConsents);
        this._api.saveConsents();
    }

    showBasicView(): void {
        this._modal?.querySelector(BASIC_VIEW_SELECTOR)?.classList.remove(HIDE_CLASS);
        this._modal?.querySelector(ADVANCED_VIEW_SELECTOR)?.classList.add(HIDE_CLASS);
        this._modal?.classList.remove(POPUP_CLASS);

        const $basicView: HTMLElement | null | undefined = this._modal?.querySelector(BASIC_VIEW_SELECTOR);
        if ($basicView) {
            $basicView.querySelector('button')?.focus();
        }
    }

    showAdvancedView(): void {
        this._modal?.querySelector(BASIC_VIEW_SELECTOR)?.classList.add(HIDE_CLASS);
        this._modal?.querySelector(ADVANCED_VIEW_SELECTOR)?.classList.remove(HIDE_CLASS);
        this._modal?.classList.add(POPUP_CLASS);
        
        const $advancedView: HTMLElement | null | undefined = this._modal?.querySelector(ADVANCED_VIEW_SELECTOR);
        if ($advancedView) {
            UiDomUtils.getFocusableElement($advancedView)?.focus();
        }
    }

    toggleAllConsents(ev: Event): void {
        const $checkbox = ev.target as HTMLInputElement;

        if ($checkbox.checked) {
            checkCheckboxes(LIST_OF_CONSENT_NAMES);
        } else {
            uncheckCheckboxes(LIST_OF_CONSENT_NAMES);
        }
    }

    saveAsDisplayed(): void {
        try {
            window.localStorage.setItem(CONSENT_MODAL_KEY, CONSENT_MODAL_COUNTER);
        } catch (err) {
            console.error(err);
        }
    }

    loadDisplayCounter(): string | null {
        return window.localStorage.getItem(CONSENT_MODAL_KEY);
    }

    close(): void {
        if (this._modal) {
            this._modal.remove();
            this._removeInertFromBodyChildrenElements();
            UiDomUtils.getFocusableElement(document.body)?.focus();
        }
    }
}
